import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  DEFAULT_ERROR_MESSAGE,
  ORIGIN,
  RESEND_TOKEN_TIME,
  TOKEN_CONFIRMATION_ERROR_MESSAGE,
  TOKEN_LENGTH,
} from 'app-constants'
import { useToaster } from 'hooks'
import { Button, ContextLoader, CountdownTime, OtpInput } from 'components'
import { passwordService } from 'services'
import { RemindLaterLink } from '../remind-later-link'
import { PageHeader } from '../page-header'
import './style.scss'

export function TokenValidation({ onContinue = () => {} }) {
  const [token, setToken] = useState('')
  const [error, setError] = useState('')
  const [canSendCode, setCanSendCode] = useState(false)
  const [contacts, setContacts] = useState({ phone: '', email: '' })
  const [loading, setLoading] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const { document } = useSelector(({ customer }) => customer.info)
  const { sendPasswordRecoverySms, confirmPasswordRecoveryToken } = passwordService()
  const { showErrorToast } = useToaster()

  useEffect(() => {
    async function initTokenVerification() {
      await handleSendConfirmationCode()
      setInitialized(true)
    }

    initTokenVerification()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (token) setError('')
  }, [token])

  function handleExpiredCode() {
    setCanSendCode(true)
  }

  async function handleSendConfirmationCode() {
    try {
      setLoading(true)
      const { data } = await sendPasswordRecoverySms({
        document,
        brand: ORIGIN,
      })
      setContacts(data)
    } catch (err) {
      console.error(err)
      const message = err.response?.data?.message || DEFAULT_ERROR_MESSAGE
      showErrorToast(message)
    } finally {
      setLoading(false)
    }
  }

  async function handleResendCode() {
    await handleSendConfirmationCode()
    setCanSendCode(false)
  }

  async function handleConfirmToken() {
    try {
      setLoading(true)
      await confirmPasswordRecoveryToken({
        token,
        customerDocument: document,
        brand: ORIGIN,
      })
      onContinue(token)
    } catch (err) {
      console.error(err)
      const message = err.response?.data?.message || TOKEN_CONFIRMATION_ERROR_MESSAGE
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  function getDisabledButton() {
    return !token || token.length !== TOKEN_LENGTH
  }

  function renderContactListItem(label, contact) {
    if (!contact) return

    return (
      <li>
        <strong>{label}:</strong> {contact}
      </li>
    )
  }

  if (!initialized) return <ContextLoader />

  return (
    <div className='token-validation-container'>
      <PageHeader />
      <p className='info'>
        Para proteger sua conta, enviamos um código de verificação para seus contatos cadastrados:
      </p>
      <ul className='contacts'>
        {renderContactListItem('Telefone', contacts.phone)}
        {renderContactListItem('E-mail', contacts.email)}
      </ul>
      <p>Digite o código recebido para confirmar a alteração da sua senha de acesso</p>
      <div className='token-input-container'>
        <div className='input-container'>
          <OtpInput error={error} maxLength={TOKEN_LENGTH} setCode={setToken} />
          <div className='error-message'>{error}</div>
        </div>

        <div className='timer-container'>
          <Button
            type='button'
            className='resend-button'
            onClick={handleResendCode}
            disabled={!canSendCode}
            color='primary'
            isLoading={loading}
          >
            Não recebi o código
          </Button>
          <div className='timer'>
            restam{' '}
            <CountdownTime
              onFinishCounter={handleExpiredCode}
              time={RESEND_TOKEN_TIME}
              canSendCode={canSendCode}
            />{' '}
            segundos
          </div>
        </div>
        <Button
          isLoading={loading}
          className='confirm-button'
          color='primary'
          onClick={handleConfirmToken}
          disabled={getDisabledButton()}
        >
          Continuar
        </Button>
        <RemindLaterLink label='sair' />
      </div>
    </div>
  )
}
